import { useEffect, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@doccareplus/shared/lib';
import { GlobalLoader } from '@doccareplus/ui';
import { useAppDispatch, useAppSelector } from '@doccareplus/kernel/application/store/hooks';
import { selectActiveContext } from '@doccareplus/kernel/application/store/slices/auth.slice';
import {
  resetBootstrap,
  runBootstrap,
  setBootstrapSteps,
  useBootstrapState,
} from '@doccareplus/kernel/application/bootstrap';
import { createWorkspaceBootstrapSteps } from './workspaceBootstrapSteps';

interface WorkspaceBootstrapGateProps {
  children: ReactNode;
  /** Overridable for testing; defaults to the real workspace steps. */
  createSteps?: typeof createWorkspaceBootstrapSteps;
}

/**
 * Runs the workspace bootstrap steps behind the global loader and only renders
 * its children once they settle — so the shell paints once, with complete
 * navigation, instead of flickering while clinic access loads. Re-runs whenever
 * the active clinic/role changes (workspace switch).
 */
export function WorkspaceBootstrapGate({
  children,
  createSteps = createWorkspaceBootstrapSteps,
}: WorkspaceBootstrapGateProps) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const activeContext = useAppSelector(selectActiveContext);
  const state = useBootstrapState();

  const contextKey = `${activeContext?.clinicId ?? ''}:${activeContext?.role ?? ''}`;
  const configuredKeyRef = useRef<string | null>(null);

  useEffect(() => {
    configuredKeyRef.current = contextKey;
    setBootstrapSteps(createSteps({ queryClient, activeContext, dispatch, t }));
    resetBootstrap();
    void runBootstrap();
    // Re-run only on workspace-context change; queryClient/t/activeContext are
    // captured fresh each run and needn't retrigger it.
    // (Directive is consumed by oxlint; eslint no longer reports here.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextKey]);

  // Show the loader until the run for the CURRENT context has settled. Guarding
  // on the configured key avoids a one-frame flash of stale "ready" state from a
  // previous context before this mount's effect re-runs the engine.
  //
  // The ref records which context the last effect actually configured, which is exactly what this guard needs and
  // what state cannot express: state written during render would already equal `contextKey` before the effect ran,
  // and state written in the effect is the `set-state-in-effect` anti-pattern. So this read is scoped out.
  const settledForContext =
    // eslint-disable-next-line react-hooks/refs
    configuredKeyRef.current === contextKey &&
    (state.status === 'ready' || state.status === 'error');

  if (!settledForContext) {
    return <GlobalLoader message={state.message ?? undefined} />;
  }

  return <>{children}</>;
}
